import { ImageIcon } from "@/components/icons";
import { Badge, Checkbox, Skeleton } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { ReorderPlanRowDTO } from "@/modules/inventory/domain/types";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import { AppImage } from "@/shared/ui/AppImage";

const PRIORITY_META: Record<
  ReorderPlanRowDTO["priority"],
  { label: string; variant: "destructive" | "secondary" | "outline" }
> = {
  critical: { label: "ດ່ວນ", variant: "destructive" },
  soon: { label: "ໃກ້ຫມົດ", variant: "secondary" },
  ok: { label: "ພໍດີ", variant: "outline" },
};

type ReorderPlanTableProps = {
  rows?: ReorderPlanRowDTO[];
  isLoading?: boolean;
  selectedIds: Set<string>;
  onToggleRow: (variantId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
};

function formatDaysOfCover(days: number | null): string {
  if (days == null) return "—";
  if (days >= 30) return `~${Math.round(days / 30)} ເດືອນ`;
  return `~${Math.round(days)} ວັນ`;
}

export function ReorderPlanTable({
  rows = [],
  isLoading,
  selectedIds,
  onToggleRow,
  onToggleAll,
}: ReorderPlanTableProps) {
  const selectableRows = rows.filter((row) => row.suggestedOrderQty > 0);
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedIds.has(row.variantId));

  if (isLoading) {
    return (
      <div className="brand-card flex flex-col gap-2 p-4">
        {["a", "b", "c", "d", "e"].map((id) => (
          <Skeleton key={id} className="h-12 rounded-lg" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="brand-card p-8 text-center">
        <p className="font-display text-lg">ບໍ່ມີລາຍການແນະນຳ</p>
        <p className="mt-1 text-muted-foreground text-sm">
          ສະຕັອກພໍດີ ຫຼື ຍັງບໍ່ມີຂໍ້ມູນການຂາຍໃນຊ່ວງທີ່ເລືອກ
        </p>
      </div>
    );
  }

  return (
    <div className="brand-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[56rem] text-sm">
          <thead>
            <tr className="border-secondary/10 border-b text-left text-muted-foreground">
              <th className="w-10 px-3 py-2 sm:px-4">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(v) => onToggleAll(v === true)}
                  aria-label="ເລືອກທັງໝົດ"
                  disabled={selectableRows.length === 0}
                />
              </th>
              <th className="px-3 py-2 font-medium sm:px-4">ສິນຄ້າ</th>
              <th className="px-3 py-2 text-right font-medium sm:px-4">
                ສຕັອກ
              </th>
              <th className="px-3 py-2 text-right font-medium sm:px-4">
                ລໍຖ້າ
              </th>
              <th className="px-3 py-2 text-right font-medium sm:px-4">
                ຂາຍ/ອາທິດ
              </th>
              <th className="px-3 py-2 text-right font-medium sm:px-4">
                ວັນຄົງເຫຼືອ
              </th>
              <th className="px-3 py-2 text-right font-medium sm:px-4">
                ແນະນຳສັ່ງ
              </th>
              <th className="px-3 py-2 font-medium sm:px-4">ຄວາມດ່ວນ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const meta = PRIORITY_META[row.priority];
              const canSelect = row.suggestedOrderQty > 0;

              return (
                <tr
                  key={row.variantId}
                  className={cn(
                    "border-secondary/5 border-b transition-colors hover:bg-muted/20",
                    selectedIds.has(row.variantId) && "bg-secondary/5",
                  )}
                >
                  <td className="px-3 py-3 sm:px-4">
                    <Checkbox
                      checked={selectedIds.has(row.variantId)}
                      onCheckedChange={(v) =>
                        onToggleRow(row.variantId, v === true)
                      }
                      disabled={!canSelect}
                      aria-label={`ເລືອກ ${row.productName}`}
                    />
                  </td>
                  <td className="px-3 py-3 sm:px-4">
                    <div className="flex min-w-0 items-center gap-3">
                      {row.imageKey ? (
                        <AppImage
                          src={row.imageKey}
                          alt={row.productName}
                          className="size-9 shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground">
                          <ImageIcon className="size-4" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate font-medium">{row.productName}</p>
                        <p className="truncate text-muted-foreground text-xs">
                          {row.variantName}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums sm:px-4">
                    {formatQtyWithUnit(row.stockQuantity, row.stockUnitLabel)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums sm:px-4">
                    {row.inTransitQuantity > 0
                      ? formatQtyWithUnit(
                          row.inTransitQuantity,
                          row.stockUnitLabel,
                        )
                      : "—"}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums sm:px-4">
                    {row.salesPerWeek > 0 ? row.salesPerWeek : "—"}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums sm:px-4">
                    {formatDaysOfCover(row.daysOfCover)}
                  </td>
                  <td className="px-3 py-3 text-right font-medium tabular-nums sm:px-4">
                    {row.suggestedOrderQty > 0
                      ? formatQtyWithUnit(
                          row.suggestedOrderQty,
                          row.stockUnitLabel,
                        )
                      : "—"}
                  </td>
                  <td className="px-3 py-3 sm:px-4">
                    <Badge variant={meta.variant}>{meta.label}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
